/**
 * Polyfills for React 19 compatibility on Web
 * - ReactDOM.findDOMNode (removed in React 19)
 * - findNodeHandle (removed in React 19)
 *
 * These allow react-native-animatable and react-native-gesture-handler
 * to work with React 19 on web
 */

import ReactDOM from 'react-dom';

/**
 * Helper function to extract DOM node from various React component types
 */
function getDOMNode(component: any): HTMLElement | null {
	if (!component) return null;

	// If it's already a DOM element, return it
	if (component instanceof HTMLElement) {
		return component;
	}

	// If it's a ref object, return the current element
	if (component && typeof component === 'object' && 'current' in component) {
		if (component.current instanceof HTMLElement) {
			return component.current;
		}
		// Recursively try to get DOM node from ref.current
		return getDOMNode(component.current);
	}

	// If it has _reactInternals (React Fiber node), walk the tree
	if (component._reactInternals) {
		const fiber = component._reactInternals;
		let node = fiber;

		// Walk up the fiber tree to find a host component (DOM node)
		while (node) {
			if (node.stateNode && node.stateNode instanceof HTMLElement) {
				return node.stateNode;
			}
			node = node.return;
		}
	}

	return null;
}

// Polyfill for ReactDOM.findDOMNode
if (typeof window !== 'undefined' && !ReactDOM.findDOMNode) {
	// @ts-ignore - Adding deprecated API for compatibility
	ReactDOM.findDOMNode = function findDOMNode(component: any) {
		// Don't warn for null/undefined - this is expected in many cases
		if (component === null || component === undefined) {
			return null;
		}

		const node = getDOMNode(component);
		// Suppress warnings for null/undefined and refs with null current (normal during unmount)
		if (!node) {
			// Don't warn for null/undefined - this is expected
			if (component === null || component === undefined) {
				return null;
			}
			// Don't warn for refs with null current - normal during unmount
			if (typeof component === 'object' && component.current === null) {
				return null;
			}
			// Only warn in development for truly unexpected cases
			if (
				__DEV__ &&
				typeof component === 'object' &&
				!('current' in component)
			) {
				// Only warn if it's not a ref pattern
				console.warn(
					'ReactDOM.findDOMNode: Could not find DOM node for:',
					component
				);
			}
		}
		return node;
	};

	console.log(
		'✅ ReactDOM.findDOMNode polyfill installed for React 19 compatibility'
	);
}

// Polyfill for findNodeHandle (used by react-native-gesture-handler)
if (typeof window !== 'undefined') {
	// Create our patched version that doesn't throw on web
	const patchedFindNodeHandle = function findNodeHandle(component: any) {
		// Use our helper to get the DOM node
		const node = getDOMNode(component);

		if (!node) {
			console.warn(
				'findNodeHandle: Could not find DOM node for:',
				component
			);
			return null;
		}

		// For web, we can return the element itself
		// Some libraries expect a number (React Native style), but on web the element works
		return node as any;
	};

	// Patch React Native's exported version BEFORE it's used
	try {
		const ReactNative = require('react-native');

		// Force override the findNodeHandle export
		Object.defineProperty(ReactNative, 'findNodeHandle', {
			value: patchedFindNodeHandle,
			writable: true,
			configurable: true,
			enumerable: true,
		});

		// Also patch on default export if it exists
		if (ReactNative.default) {
			Object.defineProperty(ReactNative.default, 'findNodeHandle', {
				value: patchedFindNodeHandle,
				writable: true,
				configurable: true,
				enumerable: true,
			});
		}

		console.log(
			'✅ findNodeHandle polyfill installed for React 19 compatibility'
		);
	} catch (error) {
		console.warn('Could not fully patch findNodeHandle:', error);
	}

	// Also try to patch global if it exists
	try {
		// @ts-ignore
		if (typeof global !== 'undefined') {
			// @ts-ignore
			global.findNodeHandle = patchedFindNodeHandle;
		}
		// @ts-ignore
		if (typeof window !== 'undefined') {
			// @ts-ignore
			window.findNodeHandle = patchedFindNodeHandle;
		}
	} catch (error) {
		// Ignore
	}
}

export {};
